import * as React from 'react'

import { useTranslation } from '../../../../app/_providers/TranslationProvider.client'
import { EmbroideryService } from '../../../../containers/EmbroideryService'
import { useEmbroideryForm } from '../../../../containers/EmbroideryService/useEmbroideryForm'
import { trackEvent } from '../../../../lib/analytics'
import { useFetchEmbroideryIconGroups } from '../../../../lib/hooks/useFetchEmbroideryIconGroups'
import { transformInitialsInput } from '../../../../lib/utils'
import { EmbroideryVariant } from '../../../../types/graphql'
import { EmbroideryTextInput } from '../EmbroideryTextInput'
import { IFormProps } from '../IFormProps'
import { IconPreviewItem } from '../PreviewItems/IconPreviewItem'
import { ColorSelector } from '../selectors/ColorSelector'
import { EmbroideryOptionWrapper, FormBody, Label } from '../shared-form-styles'
import { SubmissionButtons } from '../SubmissionButtons'

import { IconPlacement } from './FormBody/IconPlacement'
import { IconSelectorForm } from './FormBody/IconSelectorForm'
import { useValidationSchema } from './useValidationSchema'

export const IconEmbroideryForm: React.FC<IFormProps<EmbroideryVariant>> = ({
  embroideryOption,
}) => {
  const { currentlySelectedFormOption } = EmbroideryService.useContainer()

  const initialsMaxChars = embroideryOption.initialsMaxChars ?? 2
  const validationSchema = useValidationSchema({ initialsMaxChars })
  const { form, resetForm } = useEmbroideryForm('ICON_ONLY', {
    validationSchema,
    createEmbroideryOptions: values => ({
      iconDesignFile: values.iconImage?.designFile || undefined,
      iconDesignName: values.iconImage?.caption || undefined,
      iconImageSource: values.iconImage?.source || undefined,
      iconInitials: values.initials || undefined,
      iconInitialsColor: values.initialsColor,
      iconPlacement: values.iconPlacement?.name,
      embroideryItemVariant: embroideryOption.variants[0]!,
    }),
    previewItem: form => <IconPreviewItem values={form.values} />,
  })

  const {
    embroidery: {
      modal: { forms: fixtures },
    },
  } = useTranslation()

  const { embroideryIconGroups } = useFetchEmbroideryIconGroups()

  let disabledInitialsText: string | null | undefined = undefined
  const selectedIconImage = form.values.iconImage
  if (selectedIconImage && selectedIconImage.embroideryIconGroup) {
    const group = embroideryIconGroups.find(
      iconGroup => iconGroup.name === selectedIconImage.embroideryIconGroup
    )
    disabledInitialsText = group?.disabledInitialsText
  }
  const initialsDisabled = disabledInitialsText != null
  if (initialsDisabled) form.values.initials = ''

  return (
    <FormBody
      onSubmit={form.handleSubmit}
      {...trackEvent({
        category: 'embroidery',
        action: 'form submit',
        label: 'icon embroidery form',
      })}
    >
      <IconSelectorForm
        form={form}
        embroideryOptionVariants={embroideryOption.variants}
        labelId='select-icon-for-icon-only'
        embroideryIconGroups={embroideryIconGroups}
      />

      <EmbroideryOptionWrapper>
        <Label disabled={initialsDisabled}>{fixtures.iconOption.initialsLabel}</Label>
        <EmbroideryTextInput
          disabled={initialsDisabled}
          errorMessage={form.touched.initials ? form.errors.initials : undefined}
          maxCharacterCount={initialsMaxChars}
          name='initials'
          onChange={form.handleChange}
          onInput={transformInitialsInput}
          placeholder={disabledInitialsText ?? fixtures.iconOption.initialsPlaceholder}
          value={form.values.initials}
        />
      </EmbroideryOptionWrapper>

      {embroideryOption.initialsColors && (
        <EmbroideryOptionWrapper>
          <ColorSelector
            disabled={initialsDisabled}
            errorMessage={form.touched.initialsColor ? form.errors.initialsColor : undefined}
            onSelectColor={colorName => form.setFieldValue('initialsColor', colorName)}
            selectedColorKey={form.values.initialsColor}
            label={fixtures.iconOption.initialsColorLabel}
            colorNames={embroideryOption.initialsColors}
          />
        </EmbroideryOptionWrapper>
      )}

      <IconPlacement
        labelId='icon-placement-for-icon-only'
        iconPlacements={embroideryOption.iconPlacements}
        form={form}
        kind={embroideryOption.kind}
      />

      <EmbroideryOptionWrapper>
        <SubmissionButtons
          displayPrice={currentlySelectedFormOption?.variants[0]?.price}
          onReset={() => {
            resetForm()
          }}
        />
      </EmbroideryOptionWrapper>
    </FormBody>
  )
}
